import { PageCtas } from '@/Components/Main/PageCtas';
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar';
import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/Components/ui/dialog';
import useTrackVisit from '@/Hooks/useTrackVisit';
import Layout from '@/Layouts/MainLayout';
import { Head, Link } from '@inertiajs/react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
    ArrowLeft,
    Clock,
    Copy,
    Eye,
    Facebook,
    Leaf,
    Linkedin,
    Share2,
    Sprout,
    Twitter,
    Wheat,
} from 'lucide-react';
import { useState } from 'react';

interface AuthorSocials {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
    website?: string;
    email?: string;
}

interface Author {
    name: string;
    avatar: string;
    role: string;
    bio?: string;
    socials?: AuthorSocials;
}

interface Post {
    id: number;
    slug: string;
    title: string;
    content: string;
    excerpt: string;
    author: Author;
    publishedAt: string;
    tags: string[];
    heroImage: string;
    readTime: string;
    category: string;
}

interface RelatedPost {
    id: number;
    slug: string;
    title: string;
    heroImage: string;
    readTime: string;
}

interface Props {
    post: Post;
    metadata: {
        views: number;
        shares: number;
        comments_count: number;
        reading_time: string;
    };
    relatedPosts: RelatedPost[];
    topCtas: Array<{
        type:
            | 'subscribe'
            | 'contact'
            | 'register'
            | 'apply'
            | 'demo'
            | 'learn-more';
        title: string;
        description: string;
        buttonText?: string;
        href?: string;
        background: 'green' | 'white' | 'gradient' | 'image';
        backgroundImage?: string;
        overlayColor?: string;
        placement: string;
    }>;
    middleCtas: Array<{
        type:
            | 'subscribe'
            | 'contact'
            | 'register'
            | 'apply'
            | 'demo'
            | 'learn-more';
        title: string;
        description: string;
        buttonText?: string;
        href?: string;
        background: 'green' | 'white' | 'gradient' | 'image';
        backgroundImage?: string;
        overlayColor?: string;
        placement: string;
    }>;
    bottomCtas: Array<{
        type:
            | 'subscribe'
            | 'contact'
            | 'register'
            | 'apply'
            | 'demo'
            | 'learn-more';
        title: string;
        description: string;
        buttonText?: string;
        href?: string;
        background: 'green' | 'white' | 'gradient' | 'image';
        backgroundImage?: string;
        overlayColor?: string;
        placement: string;
    }>;
}

export default function Details({
    post,
    metadata,
    relatedPosts,
    topCtas,
    middleCtas,
    bottomCtas,
}: Props) {
    const [showShareToast, setShowShareToast] = useState(false);
    const [shares, setShares] = useState(metadata.shares);
    const [showAuthorModal, setShowAuthorModal] = useState(false);

    const handleShare = async (platform?: string) => {
        const url = window.location.href;
        let shareUrl = '';

        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
                break;
            default:
                await navigator.clipboard.writeText(url);
                setShowShareToast(true);
                setTimeout(() => setShowShareToast(false), 3000);
                return;
        }

        if (shareUrl) {
            window.open(shareUrl, '_blank', 'width=600,height=400');
        }

        // Increment share count
        try {
            const response = await axios.post(
                route('blog.shares.increment', post.id),
            );
            setShares(response.data.shares);
        } catch (error) {
            console.error('Error incrementing shares:', error);
        }
    };

    interface AuthorSocialButtonProps {
        href: string;
        icon: React.ElementType;
        label: string;
        className?: string;
    }

    const AuthorSocialButton = ({
        href,
        icon: Icon,
        label,
        className = '',
    }: AuthorSocialButtonProps) => (
        <Button
            variant="outline"
            className={`flex w-full items-center gap-2 ${className}`}
            onClick={() => window.open(href, '_blank')}
        >
            <Icon className="h-4 w-4" />
            {label}
        </Button>
    );

    useTrackVisit();

    return (
        <Layout>
            <Head title={post.title} />

            {/* Top CTAs */}
            <div className="container mx-auto px-4">
                <PageCtas ctas={topCtas} className="mb-8" />
            </div>

            {/* Hero Section */}
            <div className="relative mb-16 min-h-[600px]">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `linear-gradient(to right bottom, rgba(16, 185, 129, 0.7), rgba(217, 119, 6, 0.7)), url(${post.heroImage})`,
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent" />
                <div className="container relative mx-auto flex h-full items-center px-4">
                    <div className="relative z-10 max-w-4xl py-16">
                        <div className="mb-6 flex items-center gap-2">
                            <Badge className="bg-emerald-500 text-white">
                                {post.category}
                            </Badge>
                            <span className="text-white/60">•</span>
                            <span className="flex items-center gap-1 text-white/80">
                                <Clock className="h-4 w-4" />
                                {post.readTime}
                            </span>
                        </div>
                        <h1 className="mb-8 bg-gradient-to-r from-white to-white/90 bg-clip-text text-5xl font-bold leading-tight text-transparent md:text-6xl">
                            {post.title}
                        </h1>
                        <div className="flex flex-col gap-6 text-white/90 sm:flex-row sm:items-center">
                            <div className="flex items-center gap-3">
                                <Avatar className="ring-2 ring-emerald-500/20">
                                    <AvatarImage
                                        src={post.author.avatar}
                                        alt={post.author.name}
                                    />
                                    <AvatarFallback>
                                        {post.author.name[0]}
                                    </AvatarFallback>
                                </Avatar>
                                <div>
                                    <div className="font-semibold">
                                        {post.author.name}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-6 text-sm">
                                <span className="flex items-center gap-1">
                                    <Eye className="h-4 w-4" />
                                    {metadata.views} views
                                </span>
                                <span className="flex items-center gap-1">
                                    <Share2 className="h-4 w-4" />
                                    {shares} shares
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Back Button */}
            <div className="container mx-auto px-4 py-6">
                <Link href={route('blog.index')}>
                    <Button
                        variant="ghost"
                        className="mb-4 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
                    </Button>
                </Link>
            </div>

            <div className="container mx-auto max-w-7xl px-4">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    {/* Main Content */}
                    <div className="lg:col-span-8">
                        {/* Article Content */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="prose prose-lg prose-emerald dark:prose-invert prose-headings:text-emerald-900 dark:prose-headings:text-emerald-100 prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-strong:text-emerald-700 dark:prose-strong:text-emerald-300 prose-code:text-emerald-600 dark:prose-code:text-emerald-400 prose-pre:bg-emerald-50 dark:prose-pre:bg-emerald-900/30 mb-12 max-w-none"
                            dangerouslySetInnerHTML={{ __html: post.content }}
                        />

                        {/* Middle CTAs */}
                        <PageCtas ctas={middleCtas} className="my-8" />

                        {/* Tags */}
                        <div className="mb-12 flex flex-wrap gap-2">
                            {post.tags.map((tag) => (
                                <Badge
                                    key={tag}
                                    variant="secondary"
                                    className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
                                >
                                    <Leaf className="mr-1 h-3 w-3" />
                                    {tag}
                                </Badge>
                            ))}
                        </div>

                        {/* Share Section */}
                        <div className="mb-12 rounded-lg border border-emerald-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm dark:border-emerald-800/30 dark:bg-gray-800/80">
                            <h3 className="mb-4 text-lg font-semibold text-emerald-800 dark:text-emerald-200">
                                Share this article:
                            </h3>
                            <div className="flex flex-wrap gap-4">
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="flex items-center gap-2 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30"
                                    onClick={() => handleShare('facebook')}
                                >
                                    <Facebook className="h-4 w-4" />
                                    Facebook
                                </Button>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="flex items-center gap-2 hover:bg-sky-50 hover:text-sky-600 dark:hover:bg-sky-900/30"
                                    onClick={() => handleShare('twitter')}
                                >
                                    <Twitter className="h-4 w-4" />
                                    Twitter
                                </Button>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="flex items-center gap-2 hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/30"
                                    onClick={() => handleShare('linkedin')}
                                >
                                    <Linkedin className="h-4 w-4" />
                                    LinkedIn
                                </Button>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="flex items-center gap-2 hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-900/30"
                                    onClick={() => handleShare()}
                                >
                                    <Copy className="h-4 w-4" />
                                    Copy Link
                                </Button>
                            </div>
                        </div>

                        {/* Bottom CTAs */}
                        <PageCtas ctas={bottomCtas} className="mt-8" />
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-4">
                        {/* Author Card */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="mb-8 overflow-hidden rounded-lg border border-emerald-100 bg-white/80 shadow-sm backdrop-blur-sm dark:border-emerald-800/30 dark:bg-gray-800/80"
                        >
                            <div className="relative mb-16 overflow-hidden bg-gradient-to-br from-emerald-500 to-amber-500 p-6">
                                <Wheat className="absolute -right-4 -top-4 h-24 w-24 rotate-12 text-white/10" />
                                <Sprout className="absolute -left-4 bottom-0 h-24 w-24 -rotate-12 text-white/10" />
                                <div className="relative text-center">
                                    <Avatar className="mx-auto mb-4 h-24 w-24 ring-4 ring-white/20">
                                        <AvatarImage
                                            src={post.author.avatar}
                                            alt={post.author.name}
                                        />
                                        <AvatarFallback>
                                            {post.author.name[0]}
                                        </AvatarFallback>
                                    </Avatar>
                                    <h3 className="text-xl font-semibold text-white">
                                        {post.author.name}
                                    </h3>
                                </div>
                            </div>
                            <div className="p-6">
                                {post.author.bio && (
                                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                                        {post.author.bio}
                                    </p>
                                )}
                                <Button
                                    className="w-full bg-gradient-to-r from-emerald-600 to-amber-600 text-white transition-all hover:from-emerald-700 hover:to-amber-700"
                                    size="lg"
                                    onClick={() => setShowAuthorModal(true)}
                                >
                                    Follow Author
                                </Button>
                            </div>

                            {/* Author Social Links Modal */}
                            <Dialog
                                open={showAuthorModal}
                                onOpenChange={setShowAuthorModal}
                            >
                                <DialogContent className="sm:max-w-md">
                                    <DialogHeader>
                                        <DialogTitle className="text-emerald-800 dark:text-emerald-200">
                                            Connect with {post.author.name}
                                        </DialogTitle>
                                        <DialogDescription>
                                            Follow and connect with{' '}
                                            {post.author.name} on their social
                                            platforms
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="grid gap-4 py-4">
                                        {post.author.socials?.facebook && (
                                            <AuthorSocialButton
                                                href={
                                                    post.author.socials.facebook
                                                }
                                                icon={Facebook}
                                                label="Follow on Facebook"
                                                className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30"
                                            />
                                        )}
                                        {post.author.socials?.twitter && (
                                            <AuthorSocialButton
                                                href={
                                                    post.author.socials.twitter
                                                }
                                                icon={Twitter}
                                                label="Follow on Twitter"
                                                className="hover:bg-sky-50 hover:text-sky-600 dark:hover:bg-sky-900/30"
                                            />
                                        )}
                                        {post.author.socials?.linkedin && (
                                            <AuthorSocialButton
                                                href={
                                                    post.author.socials.linkedin
                                                }
                                                icon={Linkedin}
                                                label="Connect on LinkedIn"
                                                className="hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/30"
                                            />
                                        )}
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </motion.div>

                        {/* Related Posts */}
                        <div className="rounded-lg border border-emerald-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm dark:border-emerald-800/30 dark:bg-gray-800/80">
                            <h3 className="mb-6 text-lg font-semibold text-emerald-800 dark:text-emerald-200">
                                Related Articles
                            </h3>
                            <div className="space-y-6">
                                {relatedPosts.map((relatedPost) => (
                                    <Link
                                        key={relatedPost.id}
                                        href={route(
                                            'blog.show',
                                            relatedPost.slug,
                                        )}
                                        className="group block"
                                    >
                                        <div className="flex gap-4">
                                            <div className="relative h-20 w-20 overflow-hidden rounded-lg">
                                                <img
                                                    src={relatedPost.heroImage}
                                                    alt={relatedPost.title}
                                                    className="h-full w-full transform object-cover transition-transform duration-300 group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-amber-500/20" />
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-gray-800 transition-colors group-hover:text-emerald-600 dark:text-gray-200 dark:group-hover:text-emerald-400">
                                                    {relatedPost.title}
                                                </h4>
                                                <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                                                    <Clock className="h-3 w-3" />
                                                    {relatedPost.readTime}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Share Toast */}
            <AnimatePresence>
                {showShareToast && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed bottom-8 right-1/2 rounded-lg bg-gradient-to-r from-emerald-600 to-amber-600 p-4 text-white shadow-lg backdrop-blur-sm z-[100]"
                    >
                        <div className="flex items-center gap-2">
                            <Copy className="h-4 w-4" />
                            <p>Link copied to clipboard! 🎉</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Layout>
    );
}
